/**
 * Utility functions for FehlzeitModal form.
 *
 * Pure utility functions for building absence entries and date formatting.
 * Extracted from useFehlzeitModalForm.ts to reduce file size.
 *
 * @module fehlzeiten/hooks/fehlzeitFormUtils
 */

import type { FehlzeitEntry, AbsenceStatus, AbsenceSource } from "../types";

// ============================================================================
// Types
// ============================================================================

/**
 * Base entry data for building absence entries.
 */
export interface BaseEntryData {
  studentName: string;
  studentClass: string;
  courseClass: string;
  date: string;
  dateTo: string;
  timeFrom: string;
  timeTo: string;
  status: AbsenceStatus;
  reason: string;
  hasAttachment: boolean;
  hasAccess: boolean;
  sourceType: AbsenceSource;
  isExcusedLateness: boolean;
}

/**
 * Build context for creating entries.
 */
export interface BuildEntriesContext {
  formData: BaseEntryData;
  isFullDay: boolean;
  isMultipleDays: boolean;
  isRecurring: boolean;
  recurringUntil: string;
  recurringWeekdays: number[];
  selectedStudentId: string | null;
}

/**
 * Partial entry type returned by buildEntries.
 */
export type PartialFehlzeitEntry = Omit<
  FehlzeitEntry,
  "id" | "createdBy" | "createdAt" | "lastEditedBy" | "lastEditedAt"
> & {
  _student_id?: string;
  _class_id?: string;
  _recurrence?: {
    startDate: string;
    endDate: string;
    weekDays: number[];
  };
  // Form-building properties (not on FehlzeitEntry)
  timeFrom?: string;
  timeTo?: string;
};

// ============================================================================
// Date Utilities
// ============================================================================

/**
 * Formats an ISO date string (YYYY-MM-DD) to German format (DD.MM.YYYY).
 *
 * @param iso - ISO date string
 * @returns German formatted date string
 */
export function formatDateDE(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

/**
 * Parses a German date string (DD.MM.YYYY) to ISO format (YYYY-MM-DD).
 *
 * @param de - German formatted date string
 * @returns ISO date string
 */
export function parseDateDE(de: string): string {
  if (!de) return "";
  const parts = de.split(".");
  if (parts.length !== 3) return "";
  const [d, m, y] = parts;
  return `${y}-${m}-${d}`;
}

/**
 * Calculates the difference in days between two dates.
 *
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns Number of days (inclusive)
 */
export function calculateDiffDays(startDate: string, endDate: string): number {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((end.getTime() - start.getTime()) / msPerDay) + 1;
}

/**
 * Counts recurring dates matching specified weekdays.
 *
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @param weekdays - Array of weekday indices (0=Sunday, 1=Monday, etc.)
 * @returns Number of matching dates
 */
export function countRecurringDates(
  startDate: string,
  endDate: string,
  weekdays: number[],
): number {
  if (!startDate || !endDate || !weekdays.length) return 0;

  const s = new Date(startDate);
  const e = new Date(endDate);
  const days: number[] = [];

  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    days.push(d.getDay());
  }

  return days.filter((d) => weekdays.includes(d)).length;
}

// ============================================================================
// Entry Building
// ============================================================================

/**
 * Builds absence entries from form context.
 *
 * @param context - Build context with form data and settings
 * @returns Array of partial fehlzeit entries
 */
export function buildAbsenceEntries(
  context: BuildEntriesContext,
): PartialFehlzeitEntry[] {
  const {
    formData,
    isFullDay,
    isMultipleDays,
    isRecurring,
    recurringUntil,
    recurringWeekdays,
    selectedStudentId,
  } = context;

  const rawTf = formData.timeFrom ? String(formData.timeFrom).slice(0, 5) : "";
  const rawTt = formData.timeTo ? String(formData.timeTo).slice(0, 5) : "";
  // For "leave early" (only timeFrom): set timeTo=23:59 sentinel so RPC shows "ab XX:XX"
  // For "late arrival" (only timeTo): set timeFrom=00:00 sentinel so RPC shows "bis XX:XX"
  const tf = !isFullDay ? rawTf || (rawTt ? "00:00" : "") : "";
  const tt = !isFullDay ? rawTt || (rawTf ? "23:59:59" : "") : "";
  const dateRange = !isFullDay && tf && tt ? `${tf}–${tt}` : undefined;

  const baseEntry: PartialFehlzeitEntry = {
    studentName: formData.studentName,
    studentClass: formData.studentClass,
    courseClass: formData.courseClass,
    date: formatDateDE(formData.date),
    duration: isFullDay ? "Ganzer Tag" : "Zeitfenster",
    status: formData.status,
    reason: formData.reason,
    hasAttachment: formData.hasAttachment,
    hasAccess: formData.hasAccess,
    sourceType: formData.sourceType,
    isExcusedLateness:
      formData.status === "verspätet" ? formData.isExcusedLateness : undefined,
    _student_id: selectedStudentId || undefined,
    timeFrom: !isFullDay ? tf : undefined,
    timeTo: !isFullDay ? tt : undefined,
  };

  if (isRecurring) {
    return buildRecurringEntries(
      baseEntry,
      formData.date,
      recurringUntil,
      recurringWeekdays,
      dateRange,
    );
  }

  return [
    {
      ...baseEntry,
      date: formatDateDE(formData.date),
      dateRange,
      endDate:
        isMultipleDays && formData.dateTo
          ? formatDateDE(formData.dateTo)
          : undefined,
      isMultiDay: !!isMultipleDays,
    },
  ];
}

/**
 * Builds entries for recurring absences.
 */
function buildRecurringEntries(
  baseEntry: PartialFehlzeitEntry,
  startDate: string,
  recurringUntil: string,
  recurringWeekdays: number[],
  dateRange: string | undefined,
): PartialFehlzeitEntry[] {
  const s = new Date(startDate);
  const e = new Date(recurringUntil);
  const dates: string[] = [];

  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    if (recurringWeekdays.includes(d.getDay())) {
      dates.push(d.toISOString().split("T")[0]);
    }
  }

  const entries = dates.map((iso) => ({
    ...baseEntry,
    date: formatDateDE(iso),
    dateRange,
    isMultiDay: false,
  }));

  // Add recurrence metadata to first entry for backend processing
  if (entries.length > 0) {
    const startDateIso = startDate.includes("T")
      ? startDate.split("T")[0]
      : startDate;
    const endDateIso = recurringUntil.includes("T")
      ? recurringUntil.split("T")[0]
      : recurringUntil;

    console.log("[buildRecurringEntries] Adding recurrence metadata:", {
      startDate: startDateIso,
      endDate: endDateIso,
      weekDays: recurringWeekdays,
      entriesCount: entries.length,
    });

    entries[0] = {
      ...entries[0],
      _recurrence: {
        startDate: startDateIso,
        endDate: endDateIso,
        weekDays: recurringWeekdays,
      },
    };
  }

  return entries;
}
